import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { downloadFile, logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import {
  type AnalyticsExportFormatType,
  type IAnalyticsBaseParams,
  type IAnalyticsExportResponse,
} from 'entities/Analytics/api/types'
import { ExportAnalyticsModalContent } from '../ui/ExportAnalyticsModalContent'
import { ExportAnalyticsModalActions } from '../ui/ExportAnalyticsModalActions'

type IExportAnalyticsCallback<TParams> = (
  params: NoInfer<TParams> & { format: AnalyticsExportFormatType }
) => Promise<{ data: IAnalyticsExportResponse }>

export type IExportAnalyticsModalView = {
  format: AnalyticsExportFormatType | null
  setFormat: (format: AnalyticsExportFormatType) => void
  onExport: () => Promise<void>
  disabled: boolean
  loadingExport: boolean
}

export class ExportAnalyticsModalStore<TParams extends object = IAnalyticsBaseParams> {
  private _format: AnalyticsExportFormatType | null = null
  private _loadingExport = false
  private _idModal = ''
  private _exportParams: TParams | null = null

  constructor(private _exportCallback: IExportAnalyticsCallback<TParams>) {
    makeAutoObservable(this)
  }

  setFormat = (format: AnalyticsExportFormatType) => {
    this._format = format
  }

  onExport = async () => {
    try {
      if (this._format && this._exportParams) {
        this._loadingExport = true

        const params = {
          ...this._exportParams,
          format: this._format,
        }

        const {
          data: { url },
        } = await this._exportCallback(params)

        if (url) {
          toastStore.add({
            id: nanoid(),
            type: 'success',
            title: 'The export is emailed to you',
            desc: "If you don't see your file, click download",
            action: {
              onAction: () => {
                downloadFile(url)
              },
              text: 'Download',
            },
          })
        } else {
          toastStore.add({
            id: nanoid(),
            type: 'success',
            title: 'The export is emailed to you',
          })
        }
      }
    } catch (e) {
      logger.error(e)
      toastStore.add({
        id: nanoid(),
        type: 'error',
        title: 'Something went wrong, try again!',
      })
    } finally {
      this._loadingExport = false
    }
    modalStore.closeModal(this._idModal)
  }

  onOpenExport = (exportParams: TParams) => {
    this._format = null
    this._exportParams = exportParams
    this._idModal = nanoid()

    modalStore.addModal({
      id: this._idModal,
      title: 'Export data',
      ModalActions: ExportAnalyticsModalActions,
      ModalContent: ExportAnalyticsModalContent,
      ModalContentProps: { store: this },
    })
  }

  get format() {
    return this._format
  }

  get disabled() {
    return !this._format || this._loadingExport
  }

  get loadingExport() {
    return this._loadingExport
  }

  dispose = () => {
    this._format = null
    this._loadingExport = false
    this._idModal = ''
    this._exportParams = null
  }
}
